import { i, x } from './lit-element-DxWbLEw9.js';
import { getPublicWishlist } from './wishlist-api.js';
import './wishlist-products-component.js';
import { spinnerIcon } from './wishlist-icons.js';
import { generateShareUrl } from './wishlist-share-component.js';
import './wishlist-utils.js';
import './wishlist-image-grid.js';
import './wishlist-mark-as-bought-modal.js';
import './wishlist-modal-component.js';
import './wishlist-product-card-mini.js';
import './wishlist-product-card.js';
import './wishlist-manager.js';
import './wishlist-new-list-component.js';
import './wishlist-delete-confirmation-modal.js';
import './wishlist-login-prompt-component.js';
import './wishlist-navigation.js';
import './wishlist-header.js';

/**
 * Public Wishlist View Component
 * Displays a shared wishlist for public viewing
 */
class PublicWishlistViewComponent extends i {
  static properties = {
    slug: { type: String },
    wishlist: { type: Object },
    loading: { type: Boolean },
    error: { type: String },
    baseUrl: { type: String },
    fullUrl: { type: String },
    visitedWishlists: { type: Array },
  };

  constructor() {
    super();
    this.slug = null;
    this.wishlist = null;
    this.loading = false;
    this.error = null;
    this.fullUrl = null;
    this.visitedWishlists = [];
  }

  connectedCallback() {
    super.connectedCallback();
    this.loadVisitedWishlists();
    this.extractSlugFromUrl();
    if (this.slug) {
      this.generateFullUrl();
      this.fetchPublicWishlist();
    } else {
      this.error = window.wishlistTranslations?.errors?.invalidWishlistLink;
    }
  }

  /**
   * Extract the wishlist slug from the URL
   * Expected URL format: /pages/xxx?share={slug}
   */
  extractSlugFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    this.slug = urlParams.get('share');

    // If no slug in query params, check if it's in the path format /pages/wishlist/share/{slug}
    if (!this.slug) {
      const pathParts = window.location.pathname.split('/');
      if (pathParts.length >= 4 && pathParts[pathParts.length - 2] === 'share') {
        this.slug = pathParts[pathParts.length - 1];
      }
    }
  }

  /**
   * Generate the full URL with the base URL and slug
   */
  generateFullUrl() {
    if (!this.slug) return null;

    this.fullUrl = generateShareUrl(this.slug);
    return this.fullUrl;
  }

  /**
   * Load previously visited wishlists from localStorage
   */
  loadVisitedWishlists() {
    try {
      const saved = localStorage.getItem('visited_wishlists');
      this.visitedWishlists = saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error loading visited wishlists:', error);
      this.visitedWishlists = [];
    }
  }

  /**
   * Save a wishlist to the visited history in localStorage
   */
  saveToHistory(wishlist) {
    if (!wishlist || !wishlist.id || !this.slug) return;

    try {
      // Create history entry
      const entry = {
        id: wishlist.id,
        slug: this.slug,
        name: wishlist.name || `Wishlist ${wishlist.id}`,
        timestamp: new Date().toISOString(),
        url: generateShareUrl(this.slug, this.baseUrl),
      };

      // Remove duplicate if exists
      this.visitedWishlists = this.visitedWishlists.filter((item) => item.slug !== this.slug);

      // Add new entry to beginning of array
      this.visitedWishlists.unshift(entry);

      // Keep only the latest 10 entries
      if (this.visitedWishlists.length > 10) {
        this.visitedWishlists = this.visitedWishlists.slice(0, 10);
      }

      // Save to localStorage
      localStorage.setItem('visited_wishlists', JSON.stringify(this.visitedWishlists));
    } catch (error) {
      console.error('Error saving wishlist to history:', error);
    }
  }

  /**
   * Fetch the public wishlist data using the slug
   */
  async fetchPublicWishlist() {
    if (!this.slug) return;

    this.loading = true;
    this.error = null;

    try {
      const wishlist = await getPublicWishlist(this.slug);

      if (!wishlist) {
        this.error = 'Wishlist not found. It may have been removed or made private.';
      } else {
        this.wishlist = {
          ...wishlist,
          share_slug: this.slug,
        };
        this.saveToHistory(wishlist);
      }
    } catch (error) {
      console.error('Error fetching public wishlist:', error);
      this.error = error.message || 'Failed to load the wishlist. Please try again later.';
    } finally {
      this.loading = false;
      this.requestUpdate();
    }
  }

  renderHistory() {
    if (!this.visitedWishlists.length) return null;

    return x`
      <div class="mt-12 border-t border-gray-200 pt-6">
        <h2 class="mb-4 font-medium text-lg">
          ${window.wishlistTranslations?.modals?.recentlyViewedWishlists ||
          'Recently viewed wishlists'}
        </h2>
        <div class="grid gap-2">
          ${this.visitedWishlists.map(
            (item) => x`
              <a
                href="${generateShareUrl(item.slug, this.baseUrl)}"
                class="${item.slug === this.slug
                  ? 'bg-gray-100'
                  : ''} block rounded border border-gray-200 px-4 py-2 no-underline transition-colors hover:bg-gray-50"
              >
                ${item.name}
                <span class="ml-2 text-xs text-gray-500">
                  ${new Date(item.timestamp).toLocaleDateString()}
                </span>
              </a>
            `
          )}
        </div>
      </div>
    `;
  }

  render() {
    if (this.loading) {
      return x`
        <div class="flex items-center justify-center p-8">
          ${spinnerIcon('h-8 w-8 animate-spin text-primary')}
          <span class="ml-2">Loading wishlist...</span>
        </div>
      `;
    }

    if (this.error) {
      return x`
        <div class="mb-4 rounded border-l-4 border-[#e53e3e] bg-[#fff5f5] p-4">
          <p class="text-[#9b2c2c]">${this.error}</p>
        </div>
        ${this.renderHistory()}
      `;
    }

    if (!this.wishlist) {
      return x`
        <div class="mb-4 rounded border-l-4 border-[#d97706] bg-[#fffbeb] p-4">
          <p class="text-[#92400e]">No wishlist found. It may have been removed or made private.</p>
        </div>
        ${this.renderHistory()}
      `;
    }

    return x`
      <div class="public-wishlist-container">
        <wishlist-products-component
          .wishlist=${this.wishlist}
          .isPublicView=${true}
          .fullUrl=${this.fullUrl}
        ></wishlist-products-component>
        ${this.renderHistory()}
      </div>
    `;
  }

  createRenderRoot() {
    return this;
  }
}

if (!customElements.get('public-wishlist-view')) {
  customElements.define('public-wishlist-view', PublicWishlistViewComponent);
}

// Auto-initialize the component if the container exists
document.addEventListener('DOMContentLoaded', () => {
  const container = document.getElementById('public-wishlist-container');
  if (container) {
    const view = document.createElement('public-wishlist-view');
    container.appendChild(view);
  }
});

export { PublicWishlistViewComponent };
